import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Search, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { US_STATES } from "@/lib/constants";
import { decodeVin } from "@/lib/api";
import { toast } from "sonner";

export default function VinLookupCard({ variant = "hero" }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("vin");
  const [vin, setVin] = useState("");
  const [plate, setPlate] = useState("");
  const [state, setState] = useState("CA");
  const [loading, setLoading] = useState(false);
  const [decoded, setDecoded] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (mode === "vin") {
      const cleaned = vin.trim().toUpperCase();
      if (cleaned.length < 11) {
        toast.error("Please enter a valid VIN (11-17 characters).");
        return;
      }
      setLoading(true);
      try {
        const data = await decodeVin(cleaned);
        if (data?.year && data?.make && data?.model) {
          setDecoded({ ...data, vin: cleaned });
        } else {
          toast.message("Couldn't decode VIN automatically — let's continue manually.");
          navigate("/sell-your-car", { state: { prefill: { vin: cleaned } } });
        }
      } catch (err) {
        // Even if decode fails, let them continue to fill manually
        toast.message("Couldn't decode VIN automatically — let's continue manually.");
        navigate("/sell-your-car", { state: { prefill: { vin: cleaned } } });
      } finally {
        setLoading(false);
      }
    } else {
      if (!plate.trim()) {
        toast.error("Enter your license plate.");
        return;
      }
      navigate("/sell-your-car", { state: { prefill: { license_plate: plate.trim().toUpperCase(), state } } });
    }
  };

  return (
    <div
      data-testid="vin-lookup-card"
      className={`${
        variant === "dark"
          ? "glass-dark text-white rounded-none vin-dark"
          : variant === "hero"
          ? "bg-white/95 backdrop-blur-xl border border-white/40 shadow-[0_30px_80px_-20px_rgba(0,30,102,0.5)] rounded-2xl"
          : "bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl"
      } p-6 sm:p-8 w-full max-w-xl`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-blue">Free offer request</span>
        <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-slate-500 font-semibold uppercase tracking-widest">
          <ShieldCheck className="w-3 h-3" /> No SSN. No spam.
        </span>
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
        Request a <span className="text-brand-blue">cash offer</span> for your car.
      </h3>
      <p className="text-sm text-slate-600 mt-1">Enter your VIN or plate to start. We inspect in person and make a real written offer.</p>

      {decoded ? (
        <div data-testid="vin-decoded-preview" className="mt-5 space-y-4">
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-5">
            <div className="flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-wider">
              <CheckCircle2 className="w-4 h-4" /> We found your car
            </div>
            <p data-testid="vin-decoded-vehicle" className="font-display text-2xl font-extrabold text-slate-900 mt-1">
              {decoded.year} {decoded.make} {decoded.model}
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              {[decoded.trim, decoded.body_class, decoded.engine_cylinders && `${decoded.engine_cylinders}-cyl`, decoded.transmission]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <p className="text-xs text-slate-500 mt-2 font-mono tracking-wider">VIN {decoded.vin}</p>
          </div>
          <Button
            type="button"
            data-testid="vin-confirm-button"
            onClick={() => navigate("/sell-your-car", { state: { prefill: decoded } })}
            className="w-full h-14 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold text-base rounded-xl shadow-lg shadow-orange-500/30"
          >
            Yes, that's my car — continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <button
            type="button"
            data-testid="vin-not-my-car-button"
            onClick={() => setDecoded(null)}
            className="w-full text-sm font-semibold text-slate-500 hover:text-brand-blue transition-colors"
          >
            Not my car? Re-enter VIN
          </button>
        </div>
      ) : (
      <Tabs value={mode} onValueChange={setMode} className="mt-5">
        <TabsList className="bg-slate-100 rounded-full p-1 h-11">
          <TabsTrigger value="vin" data-testid="tab-vin" className="rounded-full h-9 px-5 font-semibold data-[state=active]:bg-white data-[state=active]:text-brand-blue data-[state=active]:shadow">VIN</TabsTrigger>
          <TabsTrigger value="plate" data-testid="tab-plate" className="rounded-full h-9 px-5 font-semibold data-[state=active]:bg-white data-[state=active]:text-brand-blue data-[state=active]:shadow">License Plate</TabsTrigger>
        </TabsList>

        <form onSubmit={onSubmit} className="mt-5 space-y-3">
          <TabsContent value="vin" className="m-0">
            <label htmlFor="vin-input" className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">17-character VIN</label>
            <Input
              id="vin-input"
              data-testid="vin-input"
              autoComplete="off"
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              maxLength={17}
              placeholder="e.g. 5YJ3E1EA0KF317000"
              className="h-16 px-5 text-lg font-bold uppercase tracking-wider bg-white border-2 border-slate-200 focus-visible:border-brand-blue focus-visible:ring-4 focus-visible:ring-brand-blue/20 rounded-xl placeholder:font-normal placeholder:text-slate-400 placeholder:normal-case placeholder:tracking-normal"
            />
            <p className="text-xs text-slate-500 mt-2">Find your VIN on the driver-side dashboard, door jamb, or your insurance card.</p>
          </TabsContent>
          <TabsContent value="plate" className="m-0">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">License plate</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                data-testid="plate-input"
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder="e.g. 7ABC123"
                className="h-14 sm:h-16 px-4 text-lg font-bold uppercase bg-white border-2 border-slate-200 focus-visible:border-brand-blue focus-visible:ring-4 focus-visible:ring-brand-blue/20 rounded-xl"
              />
              <Select value={state} onValueChange={setState}>
                <SelectTrigger data-testid="state-select" className="h-14 sm:h-16 sm:w-44 bg-white border-2 border-slate-200 rounded-xl font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {US_STATES.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          <Button
            type="submit"
            data-testid="get-offer-button"
            disabled={loading}
            className="w-full h-14 mt-2 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold text-base rounded-xl shadow-lg shadow-orange-500/30 disabled:opacity-70"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Decoding…</>
            ) : (
              <><Search className="w-4 h-4 mr-2" /> Start my offer request</>
            )}
          </Button>

          <p className="text-xs text-center text-slate-500">By continuing you agree to our Terms. This is a request, not a purchase offer. No credit check.</p>
        </form>
      </Tabs>
      )}
    </div>
  );
}
